import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Target, Zap, Trophy, Calendar, Users, User, Phone, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

const POSITIONS = ['Forward', 'Defense', 'Goalie'];
const LEVELS = ['Beginner', 'Intermediate', 'Advanced'];
const GENDERS = [
  { label: 'Boy', value: 'boy' },
  { label: 'Girl', value: 'girl' },
];
const GOALS = ['Improve Shot', 'Better Skating', 'Puck Handling', 'Game IQ', 'Physical Conditioning', 'Mental Toughness'];
const REFERRAL_SOURCES = ['Coach / Teammate', 'Instagram', 'Facebook', 'TikTok', 'Friend / Word of Mouth', 'Pete Lenes directly', 'Google / Search', 'Elev802 Camp / Clinic', 'Other'];

const currentYear = new Date().getFullYear();
const BIRTH_YEARS = [];
for (let y = currentYear - 5; y >= currentYear - 25; y--) {
  BIRTH_YEARS.push(y);
}

export default function OnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [position, setPosition] = useState('');
  const [level, setLevel] = useState('');
  const [gender, setGender] = useState('');
  const [goals, setGoals] = useState([]);
  const [birthYear, setBirthYear] = useState('');
  const [currentTeam, setCurrentTeam] = useState('');
  const [phone, setPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [referralSource, setReferralSource] = useState('');
  const [referralDetail, setReferralDetail] = useState('');
  const [saving, setSaving] = useState(false);

  const canNext = useCallback(() => {
    if (step === 0) return !!position;
    if (step === 1) return !!level;
    if (step === 2) return !!gender;
    if (step === 3) return goals.length > 0;
    if (step === 4) return !!birthYear;
    if (step === 5) return !!currentTeam;
    if (step === 6) return phone.replace(/\D/g, '').length >= 10 && smsOptIn; // phone + opt-in REQUIRED
    if (step === 7) return !!referralSource; // referral required
    return false;
  }, [step, position, level, gender, goals, birthYear, currentTeam, referralSource]);

  useEffect(() => {
    if (user?.onboarding_complete) navigate('/player/dashboard');
  }, [user, navigate]);

  const toggleGoal = (g) => setGoals(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);

  const handleComplete = async () => {
    setSaving(true);
    try {
      await authAPI.me(); // verify token
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/onboarding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('elev802_token')}`,
        },
        body: JSON.stringify({
          position, skill_level: level, goals,
          birth_year: parseInt(birthYear),
          current_team: currentTeam,
          phone: phone || null,
          sms_opt_in: !!(phone && smsOptIn),
          referral_source: referralSource || null,
          referral_detail: referralDetail.trim() || null,
        }),
      });
      await refreshUser();
      navigate('/player/plan-ready');
    } catch (err) {
      toast.error('Failed to save profile');
    }
    setSaving(false);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else handleComplete();
  };

  const STEPS = [
    {
      icon: <Target size={28} className="text-[#00E5FF]" />,
      title: 'POSITION',
      subtitle: 'What position do you play?',
      content: (
        <div className="grid grid-cols-1 gap-3">
          {POSITIONS.map(p => (
            <button
              key={p}
              onClick={() => setPosition(p)}
              className={`p-4 rounded-xl font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider transition-all ${
                position === p
                  ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/30'
                  : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB]'
              }`}
              data-testid={`position-${p.toLowerCase()}`}
            >{p}</button>
          ))}
        </div>
      ),
    },
    {
      icon: <Zap size={28} className="text-[#FF9100]" />,
      title: 'SKILL LEVEL',
      subtitle: 'Where are you at right now?',
      content: (
        <div className="grid grid-cols-1 gap-3">
          {LEVELS.map(l => (
            <button
              key={l}
              onClick={() => setLevel(l)}
              className={`p-4 rounded-xl font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider transition-all ${
                level === l
                  ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/30'
                  : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB]'
              }`}
              data-testid={`level-${l.toLowerCase()}`}
            >{l}</button>
          ))}
        </div>
      ),
    },
    {
      icon: <User size={28} className="text-[#F472B6]" />,
      title: 'GENDER',
      subtitle: 'This helps us place you on the right leaderboard.',
      content: (
        <div className="grid grid-cols-2 gap-3">
          {GENDERS.map(g => (
            <button
              key={g.value}
              onClick={() => setGender(g.value)}
              className={`p-4 rounded-xl font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider transition-all ${
                gender === g.value
                  ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/30'
                  : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB]'
              }`}
              data-testid={`gender-${g.value}`}
            >{g.label}</button>
          ))}
        </div>
      ),
    },
    {
      icon: <Trophy size={28} className="text-[#10B981]" />,
      title: 'GOALS',
      subtitle: 'What do you want to improve? (select all that apply)',
      content: (
        <div className="grid grid-cols-2 gap-3">
          {GOALS.map(g => (
            <button
              key={g}
              onClick={() => toggleGoal(g)}
              className={`p-3 rounded-xl text-sm font-bold transition-all ${
                goals.includes(g)
                  ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/30'
                  : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB]'
              }`}
              data-testid={`goal-${g.toLowerCase().replace(/\s+/g, '-')}`}
            >{g}</button>
          ))}
        </div>
      ),
    },
    {
      icon: <Calendar size={28} className="text-[#A78BFA]" />,
      title: 'BIRTH YEAR',
      subtitle: "When were you born?",
      content: (
        <div className="grid grid-cols-3 gap-3 max-h-[300px] overflow-y-auto pr-1">
          {BIRTH_YEARS.map(y => (
            <button
              key={y}
              onClick={() => setBirthYear(y)}
              className={`p-3 rounded-xl font-['Barlow_Condensed'] text-lg font-bold transition-all ${
                birthYear === y
                  ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/30'
                  : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#2563EB]'
              }`}
              data-testid={`birth-year-${y}`}
            >{y}</button>
          ))}
        </div>
      ),
    },
    {
      icon: <Users size={28} className="text-[#F472B6]" />,
      title: 'CURRENT TEAM',
      subtitle: "What team do you play for?",
      content: (
        <div>
          <input
            type="text"
            value={currentTeam}
            onChange={(e) => setCurrentTeam(e.target.value)}
            placeholder="Enter your team name"
            className="w-full p-4 rounded-xl bg-white border border-[#E2E8F0] text-[#0A2242] font-['Barlow_Condensed'] text-lg placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:outline-none transition-colors"
            data-testid="current-team-input"
          />
        </div>
      ),
    },
    {
      icon: <Phone size={28} className="text-[#10B981]" />,
      title: 'CELL PHONE',
      subtitle: "Required. We'll text you weekly challenge alerts and important news from your coach.",
      content: (
        <div className="space-y-3">
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            className="w-full p-4 rounded-xl bg-white border border-[#E2E8F0] text-[#0A2242] font-['Barlow_Condensed'] text-lg placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:outline-none transition-colors"
            style={{ fontSize: '16px' }}
            data-testid="phone-input"
          />
          <label className="flex items-start gap-2 px-1 cursor-pointer select-none" data-testid="sms-optin-label">
            <input
              type="checkbox"
              checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              className="mt-1 accent-[#2563EB] w-4 h-4 cursor-pointer"
              data-testid="sms-optin-checkbox"
            />
            <span className="text-xs text-[#64748B] leading-snug">
              Yes, send me coach alerts &amp; updates by text. Msg &amp; data rates may apply. Reply STOP to unsubscribe.
            </span>
          </label>
        </div>
      ),
    },
    {
      icon: <Megaphone size={28} className="text-[#00E5FF]" />,
      title: 'HOW DID YOU HEAR ABOUT US?',
      subtitle: "Help us know what's working.",
      content: (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {REFERRAL_SOURCES.map((src) => (
              <button
                key={src}
                onClick={() => setReferralSource(src)}
                className={`p-3 rounded-xl border text-left transition-all text-xs font-['Barlow_Condensed'] font-bold uppercase tracking-wide ${
                  referralSource === src
                    ? 'bg-[#00E5FF]/15 border-[#00E5FF] text-[#0A2242]'
                    : 'bg-white border-[#E2E8F0] text-[#475569] hover:border-[#00E5FF]/50'
                }`}
                data-testid={`referral-${src.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                {src}
              </button>
            ))}
          </div>
          {referralSource && (
            <input
              type="text"
              value={referralDetail}
              onChange={(e) => setReferralDetail(e.target.value)}
              placeholder={
                referralSource === 'Coach / Teammate'
                  ? "Name of the coach or teammate (optional)"
                  : referralSource === 'Friend / Word of Mouth'
                  ? "Who told you? (optional)"
                  : referralSource === 'Other'
                  ? "Tell us how (optional)"
                  : "Anything else we should know? (optional)"
              }
              className="w-full p-3 rounded-xl bg-white border border-[#E2E8F0] text-sm text-[#0A2242] placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:outline-none"
              style={{ fontSize: '16px' }}
              maxLength={120}
              data-testid="referral-detail-input"
            />
          )}
        </div>
      ),
    },
  ];

  const current = STEPS[step];

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#F8FAFC] text-[#0A2242] flex flex-col safe-top">
      {/* Progress */}
      <div className="px-6 pt-6 pb-2">
        <div className="flex gap-1.5">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-500 ${
              i <= step ? 'bg-[#2563EB]' : 'bg-[#1C2B54]'
            }`} />
          ))}
        </div>
        <p className="text-[10px] text-[#64748B] mt-2 uppercase tracking-widest">Step {step + 1} of {STEPS.length}</p>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-2">
              {current.icon}
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold tracking-tight">{current.title}</h2>
            </div>
            <p className="text-sm text-[#64748B] mb-6">{current.subtitle}</p>
            {current.content}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="px-6 pb-8 flex gap-3">
        {step > 0 && (
          <button
            onClick={() => setStep(step - 1)}
            className="p-4 rounded-xl bg-white border border-[#E2E8F0] text-[#64748B]"
            data-testid="onboarding-back"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={!canNext() || saving}
          className={`flex-1 p-4 rounded-xl font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${
            canNext()
              ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/30'
              : 'bg-[#1C2B54] text-[#64748B] cursor-not-allowed'
          }`}
          data-testid="onboarding-next"
        >
          {saving ? 'Saving...' : step === STEPS.length - 1 ? 'Complete Setup' : 'Continue'}
          {!saving && <ChevronRight size={18} />}
        </button>
      </div>
    </div>
  );
}
